/**
 * Revisar el IVA de los cobros de antes del 24/9/2026 (dirección).
 * ----------------------------------------------------------------
 * Hasta ese día, «con IVA» sumaba el 21% ENCIMA de lo escrito: 100 € se
 * guardaban como 121 €. Si lo escrito era el precio final, ese cobro quedó un
 * 21% por encima de lo cobrado de verdad. Dirección: «saca la lista y que
 * Administración diga». Aquí se decide cobro a cobro, con el importe en el botón:
 *
 *   · «Estaba bien: 121,00 €»       → no cambia nada, queda revisado.
 *   · «Era el precio final: 100,00 €» → antes de hacerlo se dice lo que cambia
 *     en la caja de ese día, en el saldo del paciente y en su presupuesto, y
 *     qué devolución deja de contar en el presupuesto por no caber ya en lo
 *     cobrado (revisión 24/9/2026). «Deshacer» la vuelve a colgar.
 *
 * Lo decidido se ve en «Revisados» y se deshace desde ahí. La cuenta y lo que
 * arrastra los hace el servidor (backend/routers/revision_iva_router.py); la
 * pantalla solo enseña lo que él dice. Los errores se quedan a la vista.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Undo2 } from 'lucide-react';
import { api, formatDate, formatDateTime, formatEUR } from '../../lib/api';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';

const URL_BASE = '/api/cobros/revision-iva';
const NOMBRE = { estaba_bien: 'Estaba bien', precio_final: 'Era el precio final' };

const detalle = (err, porDefecto) => err?.response?.data?.detail || porDefecto;

function Cambio({ etiqueta, antes, despues, testid, children }) {
  return (
    <li className="py-1.5" data-testid={testid}>
      <span className="text-muted-foreground">{etiqueta}: </span>
      <span className="tabular-nums">{formatEUR(antes)}</span>
      <span className="text-muted-foreground"> → </span>
      <strong className="tabular-nums text-[#13231E]">{formatEUR(despues)}</strong>
      {children}
    </li>
  );
}

/** La confirmación de «Era el precio final»: qué cambia, con números. */
function ConfirmarPrecioFinal({ fila, onCancelar, onHecho }) {
  const [impacto, setImpacto] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setFallo(null);
    try {
      const r = await api.get(`${URL_BASE}/${fila.id}`);
      if (!r.data?.impacto) throw new Error('sin impacto');
      setImpacto(r.data.impacto);
    } catch (err) {
      setImpacto(null);
      setFallo(detalle(err, 'No se ha podido calcular lo que cambia. No se ha tocado nada.'));
    } finally {
      setCargando(false);
    }
  }, [fila.id]);

  useEffect(() => { cargar(); }, [cargar]);

  const confirmar = async () => {
    setGuardando(true);
    setFallo(null);
    try {
      const r = await api.post(`${URL_BASE}/${fila.id}`, { decision: 'precio_final' });
      toast.success(`${fila.patient_name}: ${formatEUR(fila.con_iva_encima.total)} → ${formatEUR(fila.precio_final.total)}`);
      onHecho(r.data);
    } catch (err) {
      const msg = detalle(err, 'No se ha podido guardar. No se ha cambiado nada.');
      setFallo(msg);
      toast.error(msg);
    } finally {
      setGuardando(false);
    }
  };

  const { caja, saldo, presupuesto } = impacto || {};
  const descolgadas = impacto?.devoluciones_descolgadas || [];
  return (
    <Dialog open onOpenChange={(abierto) => { if (!abierto && !guardando) onCancelar(); }}>
      <DialogContent className="max-w-lg" data-testid="iva-confirmar">
        <DialogHeader>
          <DialogTitle>¿Era el precio final?</DialogTitle>
          <DialogDescription>
            {fila.patient_name} · {fila.concept} · {formatDate(fila.date)}
          </DialogDescription>
        </DialogHeader>

        {cargando && (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Calculando lo que cambia…
          </p>
        )}
        {impacto && (
          <ul className="text-sm divide-y divide-border" data-testid="iva-confirmar-cambios">
            <Cambio etiqueta="El cobro" antes={fila.con_iva_encima.total} despues={fila.precio_final.total}
              testid="iva-cambio-cobro">
              <span className="text-xs text-muted-foreground">
                {' '}(base {formatEUR(fila.precio_final.base)} + IVA {formatEUR(fila.precio_final.iva)})
              </span>
            </Cambio>
            {caja && (
              <Cambio etiqueta={`Caja del ${formatDate(caja.fecha)}`} antes={caja.antes} despues={caja.despues}
                testid="iva-cambio-caja" />
            )}
            {saldo && (
              <Cambio etiqueta={`Pagado por ${saldo.paciente || 'el paciente'}`} antes={saldo.pagado_antes}
                despues={saldo.pagado_despues} testid="iva-cambio-saldo">
                {saldo.pendiente_antes != null && (
                  <span>
                    <span className="text-muted-foreground"> · le queda por pagar </span>
                    <span className="tabular-nums">{formatEUR(saldo.pendiente_antes)}</span>
                    <span className="text-muted-foreground"> → </span>
                    <strong className="tabular-nums text-[#13231E]">{formatEUR(saldo.pendiente_despues)}</strong>
                  </span>
                )}
              </Cambio>
            )}
            {presupuesto && (
              <Cambio etiqueta={`Presupuesto ${presupuesto.numero}, pagado`} antes={presupuesto.pagado_antes}
                despues={presupuesto.pagado_despues} testid="iva-cambio-presupuesto">
                <span className="text-muted-foreground"> · pendiente </span>
                <span className="tabular-nums">{formatEUR(presupuesto.pendiente_antes)}</span>
                <span className="text-muted-foreground"> → </span>
                <strong className="tabular-nums text-[#13231E]">{formatEUR(presupuesto.pendiente_despues)}</strong>
                {presupuesto.pagado_entero_antes && !presupuesto.pagado_entero_despues && (
                  <span className="block text-xs text-amber-800 mt-0.5" data-testid="iva-deja-de-estar-pagado">
                    Deja de estar pagado entero.
                  </span>
                )}
              </Cambio>
            )}
            {descolgadas.map((d) => (
              <li key={d.id} className="py-1.5 text-amber-800 flex items-start gap-1.5"
                data-testid="iva-devolucion-descolgada">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                <span>
                  La devolución de <strong className="tabular-nums">{formatEUR(d.importe)}</strong> del{' '}
                  {formatDate(d.fecha)} deja de contar en el presupuesto {d.presupuesto?.numero || '—'}: ya no
                  cabe en lo cobrado en él. «Deshacer» la vuelve a colgar.
                </span>
              </li>
            ))}
            <li className="py-1.5 text-muted-foreground">
              Indicadores y Excel del mes: {formatEUR(impacto.diferencia)} menos.
            </li>
          </ul>
        )}
        {fallo && (
          <div className="text-sm rounded border border-red-200 bg-red-50 text-red-800 px-3 py-2" role="alert"
            data-testid="iva-confirmar-fallo">
            {fallo}
          </div>
        )}
        {impacto && <p className="text-xs text-muted-foreground">Se puede deshacer desde «Revisados».</p>}

        <DialogFooter>
          <Button variant="outline" onClick={onCancelar} disabled={guardando}>Cancelar</Button>
          {!impacto && !cargando ? (
            <Button variant="outline" onClick={cargar} data-testid="iva-confirmar-reintentar">Reintentar</Button>
          ) : (
            <Button onClick={confirmar} disabled={!impacto || guardando}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="iva-confirmar-si">
              {guardando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Sí, era {formatEUR(fila.precio_final.total)}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RevisionIvaPanel({ onResumen }) {
  const [vista, setVista] = useState('pendientes');
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState(null);
  // Lo que falló al decidir o deshacer: se queda puesto hasta la siguiente.
  const [falloAccion, setFalloAccion] = useState(null);
  const [ocupado, setOcupado] = useState(null);
  const [confirmando, setConfirmando] = useState(null);
  // El aviso al padre (el número del botón) por referencia: si el padre pasa
  // una función nueva en cada render, la lista no se vuelve a pedir por eso.
  const avisar = useRef(onResumen);
  avisar.current = onResumen;

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.get(URL_BASE, { params: vista === 'revisados' ? { revisados: true } : {} });
      if (!r.data || !Array.isArray(r.data.cobros)) throw new Error('respuesta sin lista');
      setDatos(r.data);
      setFallo(null);
      avisar.current?.(r.data.resumen);
    } catch (err) {
      setDatos(null);
      setFallo(err?.response?.status === 403
        ? 'Esta revisión es de dirección: no tienes permiso para verla.'
        : detalle(err, 'No se ha podido cargar la lista. Esto NO significa que no haya cobros por revisar.'));
    } finally {
      setCargando(false);
    }
  }, [vista]);

  useEffect(() => { cargar(); }, [cargar]);

  // Tras decidir o deshacer, la fila sale de la vista actual y el resumen
  // es el que devuelve el servidor.
  const quitarFila = (id, resumen) => {
    setDatos((d) => (d ? { ...d, resumen: resumen || d.resumen, cobros: d.cobros.filter((c) => c.id !== id) } : d));
    if (resumen) avisar.current?.(resumen);
  };

  const estabaBien = async (fila) => {
    setOcupado(fila.id);
    setFalloAccion(null);
    try {
      const r = await api.post(`${URL_BASE}/${fila.id}`, { decision: 'estaba_bien' });
      toast.success(`${fila.patient_name}: se queda en ${formatEUR(fila.con_iva_encima.total)}`);
      quitarFila(fila.id, r.data?.resumen);
    } catch (err) {
      const msg = detalle(err, 'No se ha podido guardar. No se ha cambiado nada.');
      setFalloAccion(`${fila.patient_name} (${formatDate(fila.date)}): ${msg}`);
      toast.error(msg);
    } finally {
      setOcupado(null);
    }
  };

  const deshacer = async (fila) => {
    setOcupado(fila.id);
    setFalloAccion(null);
    try {
      const r = await api.post(`${URL_BASE}/${fila.id}/deshacer`);
      toast.success(fila.revision?.decision === 'precio_final'
        ? `${fila.patient_name}: vuelve a ${formatEUR(fila.con_iva_encima.total)} y a «Por revisar»`
        : `${fila.patient_name}: vuelve a «Por revisar»`);
      quitarFila(fila.id, r.data?.resumen);
    } catch (err) {
      const msg = detalle(err, 'No se ha podido deshacer. No se ha cambiado nada.');
      setFalloAccion(`${fila.patient_name} (${formatDate(fila.date)}): ${msg}`);
      toast.error(msg);
    } finally {
      setOcupado(null);
    }
  };

  const resumen = datos?.resumen;
  const filas = datos?.cobros || [];
  const revisados = vista === 'revisados';

  return (
    <div data-testid="iva-panel">
      <div className="cjo-card p-4 mb-4">
        <h2 className="font-semibold text-[#13231E]">IVA de los cobros de antes del 24/9/2026</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Hasta ese día, «con IVA» sumaba el 21% encima de lo escrito: 100 € se guardaban como 121 €.
          Si lo escrito era el precio final, el cobro está un 21% por encima de lo cobrado. Decide cada uno.
        </p>
        {resumen && (
          <p className="text-sm mt-3" data-testid="iva-resumen">
            {resumen.pendientes === 0
              ? <strong className="text-[#13231E]">No queda ningún cobro por revisar.</strong>
              : (
                <>
                  <strong className="text-[#13231E]">{resumen.pendientes} {resumen.pendientes === 1 ? 'cobro' : 'cobros'} por revisar</strong>
                  <span className="text-muted-foreground"> · si todos fueran precio final, la caja bajaría </span>
                  <strong className="tabular-nums text-[#13231E]">{formatEUR(resumen.suma_diferencias)}</strong>
                </>
              )}
          </p>
        )}
        <div className="mt-3 inline-flex rounded-md border border-border overflow-hidden text-sm" role="group">
          {[['pendientes', 'Por revisar', resumen?.pendientes], ['revisados', 'Revisados', resumen?.revisados]].map(([v, txt, n]) => (
            <button key={v} type="button" onClick={() => { setFalloAccion(null); setVista(v); }}
              aria-pressed={vista === v}
              className={`px-3 py-1.5 ${vista === v ? 'bg-[#2E6E5E] text-white' : 'bg-transparent text-[#13231E] hover:bg-[rgba(46,110,94,0.06)]'}`}
              data-testid={`iva-vista-${v}`}>
              {txt}{n != null ? ` (${n})` : ''}
            </button>
          ))}
        </div>
      </div>

      {falloAccion && (
        <div className="mb-4 text-sm rounded border border-red-200 bg-red-50 text-red-800 px-3 py-2 flex items-start gap-2"
          role="alert" data-testid="iva-fallo-accion">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{falloAccion}</span>
        </div>
      )}

      <div className="cjo-card overflow-hidden">
        {cargando && !datos ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
          : fallo ? (
            <div className="py-8 px-4 text-center" data-testid="iva-fallo">
              <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-amber-600" />
              <div className="text-sm font-medium text-[#13231E]">No se ha podido cargar la revisión del IVA</div>
              <div className="text-sm text-muted-foreground mt-1">{fallo}</div>
              <Button variant="outline" size="sm" className="mt-3" onClick={cargar}>Reintentar</Button>
            </div>
          )
          : filas.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="iva-vacio">
              {revisados ? 'Todavía no se ha revisado ninguno.' : 'Nada por revisar.'}
            </div>
          )
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                  <tr>
                    <th className="py-3 px-3 font-semibold text-[#13231E]">Fecha</th>
                    <th className="py-3 px-3 font-semibold text-[#13231E]">Paciente y concepto</th>
                    <th className="py-3 px-3 font-semibold text-[#13231E]">Pago · apuntó</th>
                    <th className="py-3 px-3 font-semibold text-[#13231E] text-right">Escrito</th>
                    <th className="py-3 px-3 font-semibold text-[#13231E] text-right">Guardado (21% encima)</th>
                    <th className="py-3 px-3 font-semibold text-[#13231E] text-right">Si era precio final</th>
                    <th className="py-3 px-3 font-semibold text-[#13231E] text-right">Diferencia</th>
                    <th className="py-3 px-3 font-semibold text-[#13231E]">Presupuesto</th>
                    <th className="py-3 px-3 font-semibold text-[#13231E]">{revisados ? 'Decisión' : '¿Qué se cobró?'}</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((f) => (
                    <tr key={f.id} className="border-t border-border align-top" data-testid="iva-fila">
                      <td className="py-3 px-3 text-xs whitespace-nowrap">{formatDate(f.date)}</td>
                      <td className="py-3 px-3">
                        <div className="font-medium">{f.patient_name || '—'}</div>
                        <div className="text-xs text-muted-foreground">{f.concept}</div>
                      </td>
                      <td className="py-3 px-3 text-xs">
                        <div>{f.lineas?.length ? f.lineas.map((l) => `${formatEUR(l.amount)} ${l.payment_method}`).join(' + ') : (f.payment_method || '—')}</div>
                        <div className="text-muted-foreground">{f.created_by_name || '—'}</div>
                      </td>
                      <td className="py-3 px-3 text-right tabular-nums">{formatEUR(f.escrito)}</td>
                      <td className="py-3 px-3 text-right tabular-nums">
                        <div className="font-semibold">{formatEUR(f.con_iva_encima.total)}</div>
                        <div className="text-xs text-muted-foreground">IVA {formatEUR(f.con_iva_encima.iva)}</div>
                      </td>
                      <td className="py-3 px-3 text-right tabular-nums">
                        <div className="font-semibold">{formatEUR(f.precio_final.total)}</div>
                        <div className="text-xs text-muted-foreground">IVA {formatEUR(f.precio_final.iva)}</div>
                      </td>
                      <td className="py-3 px-3 text-right tabular-nums text-amber-800">{formatEUR(f.diferencia)}</td>
                      <td className="py-3 px-3 text-xs">{f.presupuesto ? f.presupuesto.numero : '—'}</td>
                      <td className="py-3 px-3">
                        {revisados ? (
                          <div className="flex flex-col items-start gap-1.5 min-w-[190px]">
                            <span className="text-xs" data-testid={`iva-decision-${f.id}`}>
                              <strong className="text-[#13231E]">{NOMBRE[f.revision?.decision] || f.revision?.decision}</strong>
                              {f.revision?.decision === 'precio_final' && f.total_actual != null && ` · ahora ${formatEUR(f.total_actual)}`}
                              <span className="block text-muted-foreground">
                                {f.revision?.por_nombre || '—'} · {formatDateTime(f.revision?.at)}
                              </span>
                            </span>
                            <Button size="sm" variant="outline" disabled={ocupado === f.id}
                              onClick={() => deshacer(f)} data-testid={`iva-deshacer-${f.id}`}>
                              <Undo2 className="w-3.5 h-3.5 mr-1" /> Deshacer
                            </Button>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1.5 min-w-[210px]">
                            <Button size="sm" variant="outline" disabled={ocupado === f.id}
                              onClick={() => estabaBien(f)} data-testid={`iva-bien-${f.id}`}>
                              Estaba bien: {formatEUR(f.con_iva_encima.total)}
                            </Button>
                            <Button size="sm" disabled={ocupado === f.id}
                              onClick={() => { setFalloAccion(null); setConfirmando(f); }}
                              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid={`iva-final-${f.id}`}>
                              Era el precio final: {formatEUR(f.precio_final.total)}
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {confirmando && (
        <ConfirmarPrecioFinal
          fila={confirmando}
          onCancelar={() => setConfirmando(null)}
          onHecho={(data) => { quitarFila(confirmando.id, data?.resumen); setConfirmando(null); }}
        />
      )}
    </div>
  );
}
